import { Breadcrumb, Spin, Tag, Button, Tabs, Table, Empty, Alert, Modal, Input, Form } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import AppPaths from "constants/appPaths";
import { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import actions from "store/actions";
import AttachmentCell from "components/atoms/AttachmentCell";
import AttachmentTab from "components/molecules/AttachmentTab";
import BasicFormBuilder from "components/molecules/BasicFormBuilder";
import MenuPanelContainer from "components/atoms/MenuPanelContainer";
import SidePanel from "components/atoms/SidePanel";
import LocationContainer from "./LocationMain";
import { resolveLocation } from "common/locationResolver";
import Comments from "./Comments";
import DataObjectConsents from "./DataObjectConsents";
import DataObjectVerifications from "./DataObjectVerifications";
import {
    HomeOutlined,
    PlayCircleOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    SettingOutlined,
    DownOutlined,
    UpOutlined,
    CheckCircleFilled,
    CopyOutlined,
} from "@ant-design/icons";

const TRANSFORM_LABELS = {
    RENAME_COLUMN: "Rename Column",
    FILTER_ROWS: "Filter Rows",
    CALCULATE_FIELD: "Calculate Field",
    REPLACE_VALUES: "Replace Values",
    REMOVE_DUPLICATES: "Remove Duplicates",
    SPLIT_COLUMN: "Split Column",
    MERGE_COLUMNS: "Merge Columns",
    CONVERT_DATA_TYPE: "Convert Data Type",
    TRIM_CLEAN_TEXT: "Trim/Clean Text",
    FILL_MISSING_VALUES: "Fill Missing Values",
    SORT_DATA: "Sort Data",
    REMOVE_COLUMN: "Remove Column",
    PIVOT_AGGREGATE: "Pivot / Aggregate",
};

function describeStep(step) {
    const c = step.config || {};
    switch (step.type) {
        case "RENAME_COLUMN":
            return `Rename "${c.column}" to "${c.newName}"`;
        case "FILTER_ROWS":
            return `Filter where ${c.column} ${c.operator} ${c.value ?? ""}`.trim();
        case "CALCULATE_FIELD":
            return `Calculate ${c.newColumnName} = ${c.formula}`;
        case "REPLACE_VALUES":
            return `In "${c.column}", replace "${c.findValue}" with "${c.replaceValue}"`;
        case "REMOVE_DUPLICATES":
            return `Remove duplicates on ${(c.columns || []).join(", ")}`;
        case "SPLIT_COLUMN":
            return `Split "${c.column}" by "${c.delimiter}"`;
        case "MERGE_COLUMNS":
            return `Merge ${(c.columns || []).join(", ")} into "${c.newColumnName}"`;
        case "CONVERT_DATA_TYPE":
            return `Convert "${c.column}" to ${c.targetType}`;
        case "TRIM_CLEAN_TEXT":
            return `Trim/clean "${c.column}"`;
        case "FILL_MISSING_VALUES":
            return `Fill missing in "${c.column}" using ${c.fillMethod || "value"}`;
        case "SORT_DATA":
            return `Sort by "${c.column}" ${c.direction || "asc"}`;
        case "REMOVE_COLUMN":
            return `Remove ${(c.columns || (c.column ? [c.column] : [])).join(", ")}`;
        case "PIVOT_AGGREGATE": {
            const groupBy = (c.groupBy || []).join(", ");
            const outputs = (c.aggregations || []).map(a => `${a.outputColumn} (${a.operation})`).join(", ");
            return `Group by ${groupBy || "columns"}; output ${outputs || "aggregates"}`;
        }
        default:
            return JSON.stringify(c);
    }
}

const DEFAULT_VIEW_DATA_PAGE_SIZE = 50;

const statusColors = {
    success: "green",
    failed: "red",
    pending: "gold",
    running: "blue",
    never_executed: "default",
};

export const parseJsonValue = (value) => {
    if (typeof value !== "string") return value;

    const trimmed = value.trim();
    if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) {
        return value;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

export const isAttachmentDescriptor = (item) => (
    item &&
    typeof item === "object" &&
    typeof item.key === "string" &&
    (item.name || item.originalName || item.type || item.size !== undefined)
);

export const isAttachmentValue = (value) => {
    const parsed = parseJsonValue(value);
    if (!parsed || typeof parsed !== "object") return false;

    const items = Array.isArray(parsed) ? parsed : [parsed];
    return items.length > 0 && items.every(isAttachmentDescriptor);
};

export const isMaskedValue = (value) => typeof value === "string" && /^#+$/.test(value);

export const renderViewDataCell = (value) => {
    if (value === null || value === undefined || value === "") {
        return <span className="italic text-gray-400">empty</span>;
    }

    if (isMaskedValue(value)) {
        return <AttachmentCell value={value} compact={true} showPreview={false} />;
    }

    if (isAttachmentValue(value)) {
        return <AttachmentCell value={value} compact={true} showPreview={true} />;
    }

    if (typeof value === "object") {
        return (
            <pre className="m-0 max-w-[320px] overflow-hidden text-ellipsis whitespace-nowrap text-xs">
                {JSON.stringify(value)}
            </pre>
        );
    }

    return String(value);
};

export const VERIFIED_CONSENT_KEYS = new Set(["_verified", "_consent"]);

export const renderVerifiedConsentCell = (value) => {
    if (value === null || value === undefined) {
        return <span className="italic text-gray-400">unknown</span>;
    }
    return value === true
        ? <Tag color="green">Yes</Tag>
        : <Tag color="red">No</Tag>;
};

export class ViewDetailPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            loadingData: false,
            polling: false,
            pollTimer: null,
            pipelineExpanded: false,
            loadingMore: false,
            activeTab: "data",
            executing: false,
            cloning: false,
            duplicateModalVisible: false,
            duplicateName: "",
            duplicateDescription: "",
            sourceRecordPanelVisible: false,
            sourceRecordLoading: false,
            sourceRecordError: null,
            selectedSourceRecord: null,
            requestedSourceDataObjectId: null,
            sourceRecordTab: "DATASET_RECORD",
            viewDataPageSize: DEFAULT_VIEW_DATA_PAGE_SIZE,
            viewDataCurrentPage: 1,
        };
    }

    componentDidMount() {
        const viewId = this.props.match.params.viewId;
        this.props.getViewById(viewId);
        this.props.getUserViewResourcePermission();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.match?.params?.viewId !== this.props.match.params.viewId) {
            this.setState({ viewDataCurrentPage: 1, viewDataPageSize: DEFAULT_VIEW_DATA_PAGE_SIZE });
        }

        if (prevProps.getViewByIdResult !== this.props.getViewByIdResult) {
            if (this.props.getViewByIdResult.success !== undefined) {
                this.setState({ loading: false });
            }
            if (this.props.getViewByIdResult.success === false) {
                showError("Failed to load view");
            }
            // Fetch data and history after view loads successfully
            if (this.props.getViewByIdResult.success && this.props.viewDetail && this.props.viewDetail.status !== "never_executed") {
                const viewId = this.props.match.params.viewId;
                if (!this.state.polling) {
                    this.setState({ loadingData: true });
                    this.props.getViewData(viewId);
                    this.props.getViewHistory(viewId);
                }
            }
        }

        if (prevProps.runViewResult !== this.props.runViewResult) {
            this.setState({ executing: false });
            if (this.props.runViewResult && this.props.runViewResult.success) {
                showSuccess("View execution started");
                this.startPolling();
            } else if (this.props.runViewResult && this.props.runViewResult.success === false) {
                showError("Failed to start view execution");
            }
        }

        // Check if status changed from running during polling
        if (this.state.polling && this.props.viewDetail) {
            const status = this.props.viewDetail.status;
            if (status !== "running" && status !== "pending") {
                this.stopPolling();
                if (status === "success") {
                    const viewId = this.props.match.params.viewId;
                    this.setState({ loadingData: true });
                    // View detail is already updated from polling - just fetch data and history
                    this.props.getViewData(viewId);
                    this.props.getViewHistory(viewId);
                }
            }
        }

        if (prevProps.getViewDataResult !== this.props.getViewDataResult) {
            this.setState({ loadingData: false, loadingMore: false });
        }

        if (prevProps.cloneViewResult !== this.props.cloneViewResult) {
            this.setState({ cloning: false });
            if (this.props.cloneViewResult && this.props.cloneViewResult.success) {
                showSuccess("View duplicated successfully");
                const tenant = this.props.tenant;
                if (tenant) {
                    setTimeout(() => {
                        this.props.history.push(AppPaths.TENANT_VIEWS.replace(":tenant", tenant.name));
                    }, 1500);
                }
            } else if (this.props.cloneViewResult && this.props.cloneViewResult.success === false) {
                showError("Failed to duplicate view");
            }
        }

        // --- PRE-2557: source-record side panel state, appended after the existing
        // --- view-load / execution-polling logic above so a source-record failure
        // --- cannot short-circuit stopPolling() or the view-load handlers.
        if (
            this.props.dataObjectResult !== prevProps.dataObjectResult &&
            this.state.requestedSourceDataObjectId &&
            this.state.sourceRecordPanelVisible
        ) {
            if (this.props.dataObjectResult?.success === false) {
                this.setState({
                    sourceRecordLoading: false,
                    sourceRecordError: this.props.dataObjectResult.code || "FAILED",
                });
                return;
            }

            const fetched = this.props.dataObject;
            if (fetched && fetched.dataObjectId === this.state.requestedSourceDataObjectId) {
                this.setState({ selectedSourceRecord: fetched });
            }
        }

        if (
            this.props.getSchemaModelResult !== prevProps.getSchemaModelResult &&
            this.state.requestedSourceDataObjectId &&
            this.state.sourceRecordPanelVisible &&
            this.props.getSchemaModelResult?.success === false
        ) {
            this.setState({
                sourceRecordLoading: false,
                sourceRecordError: this.props.getSchemaModelResult.code || "FAILED",
            });
            return;
        }

        // Implementation review (iteration 2) finding: updateSourceRecord's optimistic
        // merge into selectedSourceRecord was never reconciled against the actual save
        // result — a failed save (validation error, PERMISSION_DENIED, network failure)
        // displayed as if it had succeeded, with no error and no revert. Reconcile here:
        // on failure, discard the optimistic value by re-fetching the canonical record
        // and surface an error; on success, leave the optimistic value in place (a
        // re-fetch is unnecessary since the write already reflects what is displayed).
        if (
            this.props.updateDataObjectResult !== prevProps.updateDataObjectResult &&
            this.state.requestedSourceDataObjectId &&
            this.state.sourceRecordPanelVisible
        ) {
            if (this.props.updateDataObjectResult?.success === false) {
                showError("Failed to save changes to the source record.");
                const view = this.props.viewDetail;
                this.setState({ sourceRecordLoading: true, selectedSourceRecord: null });
                this.props.getDataObject(
                    view?.businessAreaId,
                    view?.sourceSchemaId,
                    view?.sourceDatasetId,
                    this.state.requestedSourceDataObjectId,
                );
            }
        }

        // Intentionally unconditional (no props-change guard) — see PRE-2557 plan
        // notes: this must re-run on every update to catch the same-tick state
        // staleness after setState({ selectedSourceRecord }) above, and to catch a
        // schema model that was already cached from an earlier fetch (no props
        // change fires for GET_SCHEMA_MODEL_REQUEST).
        if (this.isSourceRecordReady() && this.state.sourceRecordLoading) {
            this.setState({ sourceRecordLoading: false });
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    startPolling = () => {
        this.setState({ polling: true });
        const viewId = this.props.match.params.viewId;
        const timer = setInterval(() => {
            this.props.getViewById(viewId);
        }, 5000);
        this.setState({ pollTimer: timer });
        // Auto-stop after 10 minutes
        setTimeout(() => this.stopPolling(), 600000);
    };

    stopPolling = () => {
        if (this.state.pollTimer) {
            clearInterval(this.state.pollTimer);
        }
        this.setState({ polling: false, pollTimer: null });
    };

    onRunView = () => {
        const viewId = this.props.match.params.viewId;
        this.setState({ executing: true, activeTab: "history" });
        this.props.runView(viewId);
    };

    onEditView = () => {
        const tenant = this.props.tenant;
        const viewId = this.props.match.params.viewId;
        if (tenant) {
            this.props.history.push(
                AppPaths.TENANT_VIEW_EDIT.replace(":tenant", tenant.name).replace(":viewId", viewId)
            );
        }
    };

    onEditPipeline = () => {
        const tenant = this.props.tenant;
        const view = this.props.viewDetail;
        if (!tenant || !view) return;

        if (!view.pipelineId) {
            showError("This view does not have an associated pipeline");
            return;
        }
        if (!view.businessAreaName || !view.sourceSchemaName || !view.sourceDatasetId) {
            const missing = [];
            if (!view.businessAreaName) missing.push('businessAreaName');
            if (!view.sourceSchemaName) missing.push('sourceSchemaName');
            if (!view.sourceDatasetId) missing.push('sourceDatasetId');
            console.error('Missing view fields for pipeline navigation:', missing, view);
            showError("Unable to navigate to pipeline editor. Please refresh the page and try again.");
            return;
        }

        const url = AppPaths.TENANT_SCHEMA_DATASET_ID_ACTION
            .replace(":tenant", encodeURIComponent(tenant.name))
            .replace(":businessarea", encodeURIComponent(view.businessAreaName.toLowerCase()))
            .replace(":schema", encodeURIComponent(view.sourceSchemaName.toLowerCase()))
            .replace(":dataSetId", view.sourceDatasetId)
            .replace(":dataSetAction", "view")
            + `?pipelineId=${encodeURIComponent(view.pipelineId)}`;
        this.props.history.push(url);
    };

    onDuplicateView = () => {
        const view = this.props.viewDetail;
        if (view) {
            this.setState({
                duplicateModalVisible: true,
                duplicateName: `Copy of ${view.viewName}`,
                duplicateDescription: view.description || "",
            });
        }
    };

    onDuplicateModalOk = () => {
        const view = this.props.viewDetail;
        if (view) {
            this.setState({ cloning: true, duplicateModalVisible: false });
            this.props.cloneView(view.viewId, this.state.duplicateName, this.state.duplicateDescription, view.columnConfig);
        }
    };

    onDuplicateModalCancel = () => {
        this.setState({ duplicateModalVisible: false });
    };

    onLoadMore = () => {
        const viewId = this.props.match.params.viewId;
        const pageKey = this.props.viewData?.pageKey;
        if (pageKey) {
            this.setState({ loadingMore: true });
            this.props.getViewData(viewId, pageKey, 50, true);
        }
    };

    onBack = () => {
        const tenant = this.props.tenant;
        if (tenant) {
            this.props.history.push(AppPaths.TENANT_VIEWS.replace(":tenant", tenant.name));
        }
    };

    openSourceRecord = (sourceDataObjectId) => {
        const view = this.props.viewDetail;
        if (!view?.businessAreaId || !view?.sourceSchemaId || !view?.sourceDatasetId || !sourceDataObjectId) {
            showError("Unable to open source record for this view row.");
            return;
        }

        this.setState({
            sourceRecordPanelVisible: true,
            sourceRecordLoading: true,
            sourceRecordError: null,
            selectedSourceRecord: null,
            requestedSourceDataObjectId: sourceDataObjectId,
            sourceRecordTab: "DATASET_RECORD",
        });

        this.props.getDataObject(
            view.businessAreaId,
            view.sourceSchemaId,
            view.sourceDatasetId,
            sourceDataObjectId,
        );

        this.props.getSchemaModel(view.businessAreaId, view.sourceSchemaId);
        this.props.getDataObjectPermission();
        this.props.getCommentsPermission();
    };

    // Implementation review (iteration 1) finding: BasicFormBuilder no-ops silently
    // when onUpdate is undefined (components/molecules/BasicFormBuilder/index.tsx:186-235,302-308)
    // — without this, an edit-permitted user's "Save" click would silently discard the
    // edit with no error. The plan's explicit decision (LocationMain read/write parity)
    // requires this panel to actually persist edits, not just look editable.
    updateSourceRecord = (dataObjectId, dataObject) => {
        const view = this.props.viewDetail;
        this.props.updateDataObject(view?.businessAreaId, view?.sourceSchemaId, view?.sourceDatasetId, dataObjectId, dataObject);
        // Optimistic local update so the form reflects the edit immediately. This is
        // reconciled in componentDidUpdate once updateDataObjectResult resolves: on
        // failure the optimistic value is discarded and the canonical record is
        // re-fetched (see the "Implementation review (iteration 2) finding" comment
        // in componentDidUpdate) — it is not left standing unconfirmed.
        this.setState((prevState) => ({
            selectedSourceRecord: { ...prevState.selectedSourceRecord, ...dataObject },
        }));
    };

    closeSourceRecord = () => {
        this.setState({
            sourceRecordPanelVisible: false,
            sourceRecordLoading: false,
            sourceRecordError: null,
            selectedSourceRecord: null,
            requestedSourceDataObjectId: null,
        });
    };

    renderSourceRecordLink = (sourceDataObjectId) => {
        if (!sourceDataObjectId) return null;

        return (
            <Button
                type="link"
                size="small"
                onClick={() => this.openSourceRecord(sourceDataObjectId)}
            >
                {sourceDataObjectId}
            </Button>
        );
    };

    isSourceRecordReady() {
        const view = this.props.viewDetail;
        return Boolean(
            this.state.selectedSourceRecord &&
            this.props.schemaModel &&
            this.props.schemaModel.schemaId === view?.sourceSchemaId,
        );
    }

    getDataColumns = () => {
        // Get visible column IDs from columnConfig (ordered)
        const view = this.props.viewDetail;
        const visibleOrder = view?.columnConfig?.visible || [];
        const visibleIds = new Set(visibleOrder);
        const hasVisibleConfig = visibleIds.size > 0;

        // Get all available columns from the actual data
        const items = this.props.viewData?.Items || [];
        const dataKeysSet = items.length > 0
            ? new Set(Object.keys(items[0]).filter((k) => k !== "partitionKey" && k !== "sortKey" && k !== "ttl" && k !== "sourceDataObjectId"))
            : new Set();

        // Build column metadata from execution history if available
        const history = this.props.viewHistory?.Items || [];
        const latestExecution = history[0];
        const resultColumnsMap = {};
        const resultColumnsOrder = [];
        if (latestExecution && latestExecution.resultColumns) {
            const sorted = [...latestExecution.resultColumns].sort((a, b) => a.order - b.order);
            for (const col of sorted) {
                resultColumnsMap[col.fieldId] = col;
                resultColumnsMap[col.fieldName] = col;
                resultColumnsOrder.push(col.fieldId);
            }
        }

        // Use original column order from execution, filtered by visible config
        if (resultColumnsOrder.length > 0) {
            const columnsToShow = hasVisibleConfig
                ? resultColumnsOrder.filter(key => visibleIds.has(key))
                : resultColumnsOrder;

            return columnsToShow.map((key) => {
                const meta = resultColumnsMap[key];
                return {
                    title: meta?.fieldName || key,
                    dataIndex: key,
                    key: key,
                    width: 150,
                    ellipsis: true,
                    render: VERIFIED_CONSENT_KEYS.has(key) ? renderVerifiedConsentCell : renderViewDataCell,
                };
            });
        }

        // Fallback - use data keys
        if (dataKeysSet.size > 0) {
            const keysToShow = hasVisibleConfig
                ? visibleOrder.filter(k => dataKeysSet.has(k))
                : Array.from(dataKeysSet);

            return keysToShow.map((key) => {
                const meta = resultColumnsMap[key];
                return {
                    title: meta?.fieldName || key,
                    dataIndex: key,
                    key: key,
                    width: 150,
                    ellipsis: true,
                    render: VERIFIED_CONSENT_KEYS.has(key) ? renderVerifiedConsentCell : renderViewDataCell,
                };
            });
        }

        // Fallback to resultColumns if no data loaded yet
        if (latestExecution && latestExecution.resultColumns && latestExecution.resultColumns.length > 0) {
            return latestExecution.resultColumns
                .filter((col) => !hasVisibleConfig || visibleIds.has(col.fieldId) || visibleIds.has(col.fieldName))
                .sort((a, b) => a.order - b.order)
                .map((col) => ({
                    title: col.fieldName,
                    dataIndex: col.fieldId,
                    key: col.fieldId,
                    width: 150,
                    ellipsis: true,
                    render: VERIFIED_CONSENT_KEYS.has(col.fieldId) ? renderVerifiedConsentCell : renderViewDataCell,
                }));
        }

        return [];
    };

    getColumnsWithSourceRecordAction = () => {
        const columns = this.getDataColumns();
        const items = this.props.viewData?.Items || [];
        const hasSourceIds = items.some((row) => Boolean(row?.sourceDataObjectId));

        if (!hasSourceIds) return columns;

        const sourceRecordColumn = {
            title: "UID",
            dataIndex: "sourceDataObjectId",
            key: "sourceDataObjectId",
            width: 220,
            fixed: "left",
            render: this.renderSourceRecordLink,
        };

        return [sourceRecordColumn, ...columns];
    };

    render() {
        const view = this.props.viewDetail;

        if (this.state.loading) {
            return <Spin spinning={true} size="large" className="flex items-center justify-center h-full" />;
        }

        if (!view) {
            return (
                <div className="p-4">
                    <Alert message="View not found" type="error" showIcon />
                </div>
            );
        }

        return (
            <div className="flex flex-col h-full">
                {/* Breadcrumbs */}
                <div className="px-4 py-2 border-b border-gray-200">
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <HomeOutlined /> Home
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <a onClick={this.onBack}>Views</a>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>{view.viewName}</Breadcrumb.Item>
                    </Breadcrumb>
                </div>

                {/* Header */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Button icon={<ArrowLeftOutlined />} size="small" onClick={this.onBack} />
                            <div>
                                <h1 className="text-xl font-semibold m-0 flex items-center gap-2">
                                    {view.viewName}
                                    <Tag
                                        color={statusColors[view.status] || "default"}
                                        icon={view.status === "success" ? <CheckCircleFilled style={{ color: "#52c41a" }} /> : null}
                                    >
                                        {view.status}
                                    </Tag>
                                </h1>
                                <p className="text-sm text-gray-500 mt-0.5 mb-0">{view.description}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={this.onRunView}
                                loading={this.state.executing || view.status === "running"}
                                disabled={this.state.executing || view.status === "running"}
                            >
                                {this.state.executing || view.status === "running" ? "Executing..." : "Execute Now"}
                            </Button>
                            <Button icon={<EditOutlined />} onClick={this.onEditView}>
                                Edit View
                            </Button>
                            <Button icon={<CopyOutlined />} onClick={this.onDuplicateView} loading={this.state.cloning}>
                                {this.state.cloning ? "Duplicating..." : "Duplicate"}
                            </Button>
                        </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-6 mt-2 text-sm text-gray-500">
                        <span>Source: <strong>{view.sourceDatasetId}</strong></span>
                        <span>Last Executed: <strong>{view.lastExecuted ? new Date(view.lastExecuted).toLocaleString() : "Never"}</strong></span>
                        <span>Records: <strong>{(view.recordCount || 0).toLocaleString()}</strong></span>
                    </div>

                    {/* Deleted pipeline warning — only shown after execution confirmed pipeline missing */}
                    {view.pipelineId && view.pipelineStepsSnapshot && view.lastExecuted && view.pipelineVersion == null && (
                        <Alert
                            className="mt-2"
                            message="Source pipeline has been deleted. This view uses a saved copy of the pipeline steps."
                            type="warning"
                            showIcon
                            banner
                        />
                    )}

                    {/* Transformation Pipeline Summary */}
                    {view.pipelineStepsSnapshot && view.pipelineStepsSnapshot.length > 0 && (
                        <div className="mt-2 p-3 bg-white border border-gray-200 rounded">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <SettingOutlined className="text-blue-500 cursor-pointer" onClick={this.onEditPipeline} title="Edit Pipeline" />
                                    <span className="text-sm font-semibold">
                                        Transformation Pipeline ({view.pipelineStepsSnapshot.length} steps)
                                    </span>
                                </div>
                                <a
                                    onClick={() => this.setState({ pipelineExpanded: !this.state.pipelineExpanded })}
                                    className="text-sm flex items-center gap-1 text-blue-500 hover:text-blue-700"
                                >
                                    {this.state.pipelineExpanded ? <UpOutlined /> : <DownOutlined />}
                                    {this.state.pipelineExpanded ? "Collapse" : "Expand"}
                                </a>
                            </div>
                            {this.state.pipelineExpanded ? (
                                <div className="flex flex-col gap-2">
                                    {view.pipelineStepsSnapshot.map((step, index) => (
                                        <div key={index} className="flex items-start gap-2 text-sm bg-gray-50 p-2 rounded">
                                            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-800 mb-0.5">
                                                    {TRANSFORM_LABELS[step.type] || step.type}
                                                </div>
                                                <div className="text-gray-600">{describeStep(step)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                    {view.pipelineStepsSnapshot.slice(0, 3).map((step, index) => (
                                        <span key={index}>
                                            {TRANSFORM_LABELS[step.type] || step.type}
                                            {index < Math.min(2, view.pipelineStepsSnapshot.length - 1) && " → "}
                                        </span>
                                    ))}
                                    {view.pipelineStepsSnapshot.length > 3 && (
                                        <span> ... +{view.pipelineStepsSnapshot.length - 3} more</span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex-1 overflow-auto px-4 py-2">
                    <Tabs activeKey={this.state.activeTab} onChange={(key) => this.setState({ activeTab: key })}>
                        <Tabs.TabPane tab="View Data" key="data">
                            {view.status === "never_executed" ? (
                                <Empty description="This view has not been executed yet. Click 'Execute Now' to generate results." />
                            ) : (
                                <>
                                    <Table
                                        columns={this.getColumnsWithSourceRecordAction()}
                                        dataSource={this.props.viewData?.Items || []}
                                        rowKey={(row, idx) => row?.sourceDataObjectId || idx}
                                        size="small"
                                        loading={this.state.loadingData}
                                        pagination={{
                                            pageSize: this.state.viewDataPageSize,
                                            current: this.state.viewDataCurrentPage,
                                            onChange: (page, pageSize) =>
                                                this.setState((prev) => ({
                                                    viewDataPageSize: pageSize,
                                                    viewDataCurrentPage: pageSize !== prev.viewDataPageSize ? 1 : page,
                                                })),
                                        }}
                                        scroll={{ x: "max-content" }}
                                        locale={{ emptyText: this.state.loadingData ? " " : "No data available" }}
                                    />
                                    {this.props.viewData?.pageKey && (
                                        <div className="text-center mt-3 mb-2">
                                            <Button
                                                onClick={this.onLoadMore}
                                                loading={this.state.loadingMore}
                                                disabled={this.state.loadingMore}
                                            >
                                                Load More Rows ({(this.props.viewData.Items?.length || 0).toLocaleString()} of {(view.recordCount || 0).toLocaleString()} loaded)
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Metadata & History" key="history">
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">View ID</div>
                                    <div className="text-base font-mono">{view.viewId}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">Source Schema</div>
                                    <div className="text-base">{view.sourceSchemaId}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">Source Dataset</div>
                                    <div className="text-base">{view.sourceDatasetId}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">Pipeline</div>
                                    <div className="text-base">{view.pipelineId || "None"}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">Created</div>
                                    <div className="text-base">{new Date(view.createdDate).toLocaleString()}</div>
                                </div>
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-sm text-gray-500">Last Updated</div>
                                    <div className="text-base">{new Date(view.updatedDate).toLocaleString()}</div>
                                </div>
                            </div>

                            <h3 className="text-base font-semibold mb-2">Execution History</h3>
                            <Table
                                columns={[
                                    {
                                        title: "Timestamp",
                                        dataIndex: "executionTimestamp",
                                        key: "executionTimestamp",
                                        render: (ts) => ts ? new Date(ts).toLocaleString() : "",
                                    },
                                    {
                                        title: "Status",
                                        dataIndex: "status",
                                        key: "status",
                                        render: (status) => (
                                            <Tag color={statusColors[status] || "default"}>{status}</Tag>
                                        ),
                                    },
                                    {
                                        title: "Records",
                                        dataIndex: "recordCount",
                                        key: "recordCount",
                                        render: (count) => (count || 0).toLocaleString(),
                                    },
                                    {
                                        title: "Duration",
                                        dataIndex: "executionTimeMs",
                                        key: "executionTimeMs",
                                        render: (ms) => ms ? `${(ms / 1000).toFixed(1)}s` : "N/A",
                                    },
                                    {
                                        title: "Steps",
                                        dataIndex: "stepsExecuted",
                                        key: "stepsExecuted",
                                    },
                                    {
                                        title: "Error",
                                        dataIndex: "errorMessage",
                                        key: "errorMessage",
                                        ellipsis: true,
                                        render: (msg) => msg ? <span className="text-red-500">{msg}</span> : null,
                                    },
                                ]}
                                dataSource={this.props.viewHistory?.Items || []}
                                rowKey="executionTimestamp"
                                size="small"
                                pagination={false}
                                locale={{ emptyText: "No execution history" }}
                            />
                        </Tabs.TabPane>
                    </Tabs>
                </div>

                <Modal
                    title="Duplicate View"
                    open={this.state.duplicateModalVisible}
                    onOk={this.onDuplicateModalOk}
                    onCancel={this.onDuplicateModalCancel}
                    okText="Duplicate"
                    okButtonProps={{ disabled: !this.state.duplicateName.trim() }}
                >
                    <Form layout="vertical">
                        <Form.Item label="View Name" required>
                            <Input
                                value={this.state.duplicateName}
                                onChange={(e) => this.setState({ duplicateName: e.target.value })}
                                placeholder="Enter view name"
                            />
                        </Form.Item>
                        <Form.Item label="Description">
                            <Input.TextArea
                                value={this.state.duplicateDescription}
                                onChange={(e) => this.setState({ duplicateDescription: e.target.value })}
                                placeholder="Enter description (optional)"
                                rows={3}
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                {this.renderSourceRecordPanel()}
            </div>
        );
    }

    renderLocationContainer = () => {
        const schemaModel = this.props.schemaModel;
        const record = this.state.selectedSourceRecord;
        const result = resolveLocation(schemaModel?.fields || [], record);

        const commonProps = {
            businessAreaId: schemaModel?.businessAreaId,
            schemaId: schemaModel?.schemaId,
            dataSetId: this.props.viewDetail?.sourceDatasetId,
            selectedDataObject: record,
        };

        if (result.type === "markers") {
            return <LocationContainer markers={result.data} {...commonProps} />;
        }

        if (result.type === "address") {
            return <LocationContainer locationAddress={result.data} {...commonProps} />;
        }

        const alertTypeMap = {
            MASKED_DATA: "info",
            EMPTY_VALUE: "warning",
            INVALID_COORDINATES: "error",
            NULL_ISLAND: "warning",
            JUNK_VALUE: "warning",
            INSUFFICIENT_PARTS: "warning",
            NO_FIELDS: "info",
        };

        return <Alert type={alertTypeMap[result.error.code] || "warning"} message={result.error.message} showIcon style={{ margin: "20px" }} />;
    };

    renderSourceRecordPanel() {
        if (!this.state.sourceRecordPanelVisible) return null;

        // PRE-2557: recordKeys must be computed here, inside render (not as a class
        // field or in the constructor), because getCommentsPermission() is dispatched
        // from openSourceRecord — i.e. after mount — so userCommentsPermission is
        // undefined until that fetch resolves. A class-field recordKeys would capture
        // COMMENTS absent permanently. Follows the pattern at DataSets.jsx:1242-1249.
        const recordKeys = {
            DATASET_RECORD: "Record",
            ATTACHMENTS: "Attachments",
            DATA_OBJECT_VERIFICATION: "Verification",
            DATA_OBJECT_CONSENT: "Consent",
            LOCATION_ACTIVE: "Location",
            ...(this.props.userCommentsPermission?.canView ? { COMMENTS: "Comments" } : {}),
        };

        const ready = this.isSourceRecordReady();
        const record = this.state.selectedSourceRecord;
        const schemaModel = this.props.schemaModel;

        return (
            <SidePanel onClose={this.closeSourceRecord}>
                {this.state.sourceRecordError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="Unable to load source record"
                        description={this.state.sourceRecordError}
                    />
                ) : !ready ? (
                    <Spin spinning={true} size="large" className="flex items-center justify-center h-full" />
                ) : (
                    <MenuPanelContainer
                        recordKeys={recordKeys}
                        selectedKey={this.state.sourceRecordTab}
                        onChange={(key) => this.setState({ sourceRecordTab: key })}
                    >
                        {this.state.sourceRecordTab === "DATASET_RECORD" && (
                            <BasicFormBuilder
                                key={record.dataObjectId}
                                showHeader={false}
                                formDataObject={record}
                                schemaFields={schemaModel?.fields || []}
                                onUpdate={this.updateSourceRecord}
                                requestVerification={true}
                                showRevealButton={true}
                                readOnly={!this.props.dataObjectPermission?.canEdit}
                            />
                        )}
                        {this.state.sourceRecordTab === "ATTACHMENTS" && (
                            <AttachmentTab
                                dataObject={record}
                                schemaFields={schemaModel?.fields || []}
                            />
                        )}
                        {this.state.sourceRecordTab === "DATA_OBJECT_VERIFICATION" && (
                            <DataObjectVerifications
                                key={record.dataObjectId}
                                businessAreaId={record.businessAreaId}
                                schemaId={record.schemaId}
                                dataObjectId={record.dataObjectId}
                            />
                        )}
                        {this.state.sourceRecordTab === "DATA_OBJECT_CONSENT" && (
                            <DataObjectConsents
                                key={record.dataObjectId}
                                dataObjectId={record.dataObjectId}
                            />
                        )}
                        {this.state.sourceRecordTab === "LOCATION_ACTIVE" && this.renderLocationContainer()}
                        {this.props.userCommentsPermission?.canView && this.state.sourceRecordTab === "COMMENTS" && (
                            <Comments
                                key={record.dataObjectId}
                                recordId={record.dataObjectId}
                                permission={this.props.userCommentsPermission}
                                recordType="dataobject"
                            />
                        )}
                    </MenuPanelContainer>
                )}
            </SidePanel>
        );
    }
}

const mapStateToProps = (state) => ({
    tenant: state.userLogin.tenant,
    viewDetail: state.views.viewDetail,
    getViewByIdResult: state.views.getViewByIdResult,
    runViewResult: state.views.runViewResult,
    viewData: state.views.viewData,
    getViewDataResult: state.views.getViewDataResult,
    viewHistory: state.views.viewHistory,
    resourcePermission: state.views.resourcePermission,
    cloneViewResult: state.views.cloneViewResult,
    // PRE-2557: source-record side panel
    dataObject: state.dataObjects.dataObject,
    dataObjectResult: state.dataObjects.getDataObjectResult,
    schemaModel: state.schemaModels.schemaModel,
    getSchemaModelResult: state.schemaModels.getSchemaModelResult,
    dataObjectPermission: state.dataObjects.dataObjectPermission,
    userCommentsPermission: state.comments.userCommentsPermission,
    updateDataObjectResult: state.dataObjects.updateDataObjectResult,
});

const mapDispatchToProps = (dispatch) => ({
    getViewById: (viewId) => dispatch(actions.getViewByIdRequest(viewId)),
    runView: (viewId) => dispatch(actions.runViewRequest(viewId)),
    cloneView: (viewId, viewName, description, columnConfig) => dispatch(actions.cloneViewRequest(viewId, viewName, description, columnConfig)),
    getViewData: (viewId, pageKey, limit, append) => dispatch(actions.getViewDataRequest(viewId, pageKey, limit, append)),
    getViewHistory: (viewId) => dispatch(actions.getViewHistoryRequest(viewId)),
    getUserViewResourcePermission: () => dispatch(actions.getUserViewResourcePermissionRequest()),
    // PRE-2557: source-record side panel
    getDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId) => dispatch(actions.getDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId)),
    getSchemaModel: (businessAreaId, schemaId) => dispatch(actions.getSchemaModelRequest(businessAreaId, schemaId)),
    getDataObjectPermission: () => dispatch(actions.getDataObjectPermissionRequest()),
    getCommentsPermission: () => dispatch(actions.getCommentsPermissionRequest()),
    updateDataObject: (businessAreaId, schemaId, dataSetId, dataObjectId, dataObject) =>
        dispatch(actions.updateDataObjectRequest(businessAreaId, schemaId, dataSetId, dataObjectId, dataObject)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViewDetailPage));
